import PageTitle from '@/components/PageTitle';
import ThemeWrapper from '@/components/ThemeWrapper';
import Heading from '@/components/Typography/Heading';
import AppLayout from '@/layouts/AppLayout';
import { usePage } from '@inertiajs/react';
import { CheckCircle, ExternalLink } from 'lucide-react';
import React from 'react';

interface FirmItem {
    name: string;
    features: string[];
    link_url: string;
    link_text?: string;
    highlight?: boolean;
    logo_url?: string | null;
    cta_title?: string;
    cta_button_text?: string;
    cta_button_url?: string;
}

interface Section {
    title: string;
    items: FirmItem[];
}

interface PageConfig {
    title: string;
    sections: Section[];
}

interface PageProps {
    static_page: {
        id: number;
        name: string;
        slug: string;
    };
    config: PageConfig;
}

const FirmCard: React.FC<{ firm: FirmItem }> = ({ firm }) => {
    return (
        <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm md:flex dark:border-neutral-700 dark:bg-neutral-800">
            {/* Logo panel */}
            <div className="flex w-full items-center justify-center border-b border-gray-100 p-8 md:w-52 md:flex-shrink-0 md:border-r md:border-b-0 dark:border-neutral-700">
                {firm.logo_url ? (
                    <img
                        src={firm.logo_url}
                        alt={firm.name}
                        className="max-h-24 w-full object-contain"
                    />
                ) : (
                    <span className="text-lg font-bold text-gray-900 dark:text-white">
                        {firm.name}
                    </span>
                )}
            </div>

            {/* Content */}
            <div className="flex flex-1 flex-col justify-between p-6">
                <div>
                    <Heading as="h3" className="mb-3">
                        {firm.name}
                    </Heading>
                    <ul className="space-y-1.5">
                        {firm.features.map((feature, i) => (
                            <li
                                key={i}
                                className="flex items-start gap-2 text-base text-gray-700 dark:text-neutral-300"
                            >
                                <CheckCircle className="mt-0.5 h-4 w-4 flex-shrink-0 text-green-500" />
                                <span>{feature}</span>
                            </li>
                        ))}
                    </ul>
                </div>
                {firm.link_url && (
                    <div className="mt-5">
                        <a
                            href={firm.link_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="primary-button inline-flex w-full items-center justify-center gap-2"
                        >
                            <ExternalLink className="h-4 w-4" />
                            {firm.link_text ? firm.link_text : 'ACCEDE AQUÍ'}
                        </a>
                    </div>
                )}
            </div>
        </div>
    );
};

const FirmCtaBanner: React.FC<{ firm: FirmItem }> = ({ firm }) => {
    return (
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-6 dark:border-amber-800 dark:bg-amber-950/40">
            <Heading as="h2" className="mb-3 text-xl">
                {firm.cta_title}
            </Heading>
            {firm.cta_button_url && (
                <a
                    href={firm.cta_button_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="primary-button inline-flex items-center gap-2"
                >
                    {firm.cta_button_text ? firm.cta_button_text : 'Ver más'}
                </a>
            )}
        </div>
    );
};

const FirmasPropietariasContent: React.FC = () => {
    const { static_page, config } = usePage<PageProps>().props;
    const title = config?.title ?? static_page.name;
    const sections = config?.sections ?? [];

    return (
        <AppLayout>
            <PageTitle title={static_page.name} />
            <div className="mx-auto max-w-5xl space-y-6">
                <Heading as="h1" className="text-4xl sm:text-5xl">
                    {title}
                </Heading>

                {sections.map((section, i) => (
                    <div key={i} className="space-y-3">
                        <Heading as="h2" className="text-3xl">
                            {section.title}
                        </Heading>
                        <div className="space-y-3">
                            {section.items.map((firm, j) => (
                                <React.Fragment key={j}>
                                    <FirmCard firm={firm} />
                                    {firm.cta_title ? (
                                        <FirmCtaBanner firm={firm} />
                                    ) : null}
                                </React.Fragment>
                            ))}
                        </div>
                    </div>
                ))}
            </div>
        </AppLayout>
    );
};

const FirmasPropietarias: React.FC = () => (
    <ThemeWrapper>
        <FirmasPropietariasContent />
    </ThemeWrapper>
);

export default FirmasPropietarias;
